import React, { useState } from 'react';
import DocsCodeBlock from '../Components/Docs/DocsCodeBlock';
import DocsCodePreview from '../Components/Docs/DocsCodePreview';
import DocsFigure from '../Components/Docs/DocsFigure';
import DocsGuide from '../Components/Docs/DocsGuide';
import heroDevices from '../Assets/docs/hero-devices.svg';

const EXAMPLE_GALLERY =
  'https://try.magicfurnish.com/floor-gallery/507f1f77bcf86cd799439011';

const INLINE_IFRAME_TEXT = `<div class="mf-floorplan">
  <iframe
    title="Furnished floor plan"
    src="${EXAMPLE_GALLERY}"
    style="width: 100%; height: 100%; border: 0;"
    loading="lazy">
  </iframe>
</div>`;

const INLINE_CSS_TEXT = `.mf-floorplan {
  width: 100%;
  max-width: 1200px;
  height: 70vh;
  min-height: 480px;
}`;

const DIALOG_TEXT = `<button type="button" id="mf-open">View furnished floor plan</button>

<div id="mf-dialog" hidden>
  <button type="button" id="mf-close">Close</button>
  <iframe
    id="mf-frame"
    title="Furnished floor plan"
    style="width: 100%; height: 100%; border: 0;">
  </iframe>
</div>

<style>
  #mf-dialog {
    position: fixed;
    inset: 2.5vh 2.5vw;
    z-index: 1000;
    background: #fff;
  }
  #mf-dialog[hidden] { display: none; }
</style>

<script>
  var dialog = document.getElementById('mf-dialog');
  var frame = document.getElementById('mf-frame');
  var galleryUrl = '${EXAMPLE_GALLERY}';

  document.getElementById('mf-open').addEventListener('click', function () {
    frame.src = galleryUrl;
    dialog.hidden = false;
    document.documentElement.style.overflow = 'hidden';
  });

  document.getElementById('mf-close').addEventListener('click', function () {
    dialog.hidden = true;
    frame.removeAttribute('src');
    document.documentElement.style.overflow = '';
  });
</script>`;

const LOOKUP_RESPONSE_TEXT = `{
  "ref": "LISTING-1042",
  "status": "ready",
  "galleryUrl": "${EXAMPLE_GALLERY}",
  "uniqueId": "f47ac10b-58cc-4372-a567-0e02b2c3d479",
  "hasOlderLayouts": false,
  "olderLayoutsCount": 0
}`;

const DOC_NAV_GROUPS = [
  {
    heading: 'Start here',
    primary: true,
    items: [
      { id: 'quickstart', label: 'Quickstart' },
      { id: 'place', label: 'Place on the page' },
      { id: 'dialog', label: 'Open over the page' },
      { id: 'linkout', label: 'Full plan link' },
      { id: 'checklist', label: 'Embed checklist' },
    ],
  },
  {
    heading: 'Contract reference',
    items: [
      { id: 'parameters', label: 'The URL' },
      { id: 'galleries', label: 'Replacing a plan' },
    ],
  },
  {
    heading: 'Security & operations',
    items: [
      { id: 'security', label: 'Security' },
      { id: 'troubleshooting', label: 'Troubleshooting' },
    ],
  },
  {
    heading: 'Additional resources',
    items: [
      { id: 'platform', label: 'On a phone' },
      { id: 'sample', label: 'Sample page' },
      { id: 'create', label: 'Creating the plan' },
    ],
  },
  {
    heading: 'Production launch',
    items: [
      { id: 'publishing', label: 'On the listing' },
      { id: 'kickoff', label: 'Launch inputs' },
    ],
  },
];

const SAMPLE_PAGE_TEXT = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>MagicFurnish listing embed</title>
  <style>
    body { font-family: sans-serif; margin: 0; padding: 24px; }
    .mf-floorplan { width: 100%; max-width: 1200px; height: 70vh; min-height: 480px; }
    iframe { width: 100%; height: 100%; border: 0; }
  </style>
</head>
<body>
  <p><a id="full" href="${EXAMPLE_GALLERY}">Open the full plan</a></p>
  <div class="mf-floorplan">
    <iframe title="Furnished floor plan" src="${EXAMPLE_GALLERY}"></iframe>
  </div>
</body>
</html>`;

const EmbedGuideDocs = () => {
  const [dialogExpanded, setDialogExpanded] = useState(false);
  const [sampleExpanded, setSampleExpanded] = useState(false);

  return (
    <DocsGuide
      documentTitle="MagicFurnish Listing Embed"
      navGroups={DOC_NAV_GROUPS}
      lastUpdated="November 4, 2026"
    >
      <header className="docs-hero">
        <p className="docs-hero-eyebrow">Listing Embed Guide</p>
        <h1 className="doc-title">
          One link on the listing.
          <br />
          The furnished floor plan.
        </h1>
        <p className="doc-sub">
          Show a stored <code>galleryUrl</code> on a property site or listing platform. Shoppers
          see the furnished floor plan on the listing, with no MagicFurnish account. The page
          inside the frame is the MagicFurnish gallery.
        </p>
      </header>

      <DocsFigure
        src={heroDevices}
        frame={false}
        className="docs-hero-figure"
        aspectRatio="16 / 9"
        alt="MagicFurnish floor plan gallery on desktop and mobile."
      />

      <section className="quickstart" id="quickstart">
        <h2 className="quickstart-title">Quickstart</h2>
        <ol className="quickstart-steps">
          <li>
            <a href="#parameters"><strong>Store the URL</strong></a>: keep the{' '}
            <code>galleryUrl</code> MagicFurnish returned for this listing. Use that string as-is.
          </li>
          <li>
            <a href="#place"><strong>Place it on the listing</strong></a>: set the iframe{' '}
            <code>src</code> to that URL, inline on the page or in a dialog.
          </li>
          <li>
            <a href="#linkout"><strong>Link to the full plan</strong></a>: offer the same URL as a
            normal link so shoppers can open it on its own page.
          </li>
        </ol>
      </section>

      <section className="phase" id="phase-1">
        <div className="step" id="place">
          <div className="step-head">
            <span className="step-num">01</span>
            <span className="step-name">Place the plan on the page</span>
          </div>
          <p>
            Use this when the floor plan sits in the listing, in the same column as the photos and
            description. Set <code>src</code> to the stored <code>galleryUrl</code>. The example
            below uses a sample host and id. Replace <code>src</code> with the URL for the listing.
          </p>
          <DocsCodeBlock text={INLINE_IFRAME_TEXT} label="iframe">
            &lt;<span className="k">div</span> <span className="k">class</span>=<span className="s">&quot;mf-floorplan&quot;</span>&gt;
            {'\n  '}
            &lt;<span className="k">iframe</span>
            {'\n    '}
            <span className="k">title</span>=<span className="s">&quot;Furnished floor plan&quot;</span>
            {'\n    '}
            <span className="k">src</span>=<span className="s">&quot;{EXAMPLE_GALLERY}&quot;</span>
            {'\n    '}
            <span className="k">style</span>=<span className="s">&quot;width: 100%; height: 100%; border: 0;&quot;</span>
            {'\n    '}
            <span className="k">loading</span>=<span className="s">&quot;lazy&quot;</span>&gt;
            {'\n  '}
            &lt;/<span className="k">iframe</span>&gt;
            {'\n'}
            &lt;/<span className="k">div</span>&gt;
          </DocsCodeBlock>
          <p>
            The gallery fills the iframe. Give the container a real height, or the plan is clipped
            and the listing page scrolls inside the frame.
          </p>
          <DocsCodeBlock text={INLINE_CSS_TEXT} label="container">
            .<span className="k">mf-floorplan</span> {'{'}
            {'\n  '}
            <span className="k">width</span>: <span className="s">100%</span>;
            {'\n  '}
            <span className="k">max-width</span>: <span className="s">1200px</span>;
            {'\n  '}
            <span className="k">height</span>: <span className="s">70vh</span>;
            {'\n  '}
            <span className="k">min-height</span>: <span className="s">480px</span>;
            {'\n}'}
          </DocsCodeBlock>
          <div className="docs-note">
            <code>loading=&quot;lazy&quot;</code> fits a plan below the fold. Leave it off when the
            plan is the first thing on the page. Leave <code>sandbox</code> unset.
          </div>
          <p>
            If the listing page sends a Content Security Policy, <code>frame-src</code> must allow
            the origin of <code>galleryUrl</code>. A source of{' '}
            <code>https://*.magicfurnish.com</code> covers MagicFurnish hosts.
          </p>
        </div>

        <div className="step" id="dialog">
          <div className="step-head">
            <span className="step-num">02</span>
            <span className="step-name">Open the plan over the page</span>
          </div>
          <p>
            Use this when the listing is a card, a photo gallery, or a narrow column. A button
            opens the plan in a dialog that fills most of the screen. Mount the iframe when the
            dialog opens, so a page of many listings does not load every plan at once.
          </p>
          <ul>
            <li>Label the control as a floor plan, in the listing site&apos;s own words.</li>
            <li>Size the dialog to about 95% of the viewport width and height.</li>
            <li>Put Close on the dialog, outside the iframe, and return focus to the button.</li>
            <li>Stop the listing page from scrolling while the dialog is open. Close on Escape.</li>
          </ul>
          <DocsCodePreview
            label="dialog"
            expanded={dialogExpanded}
            onToggle={() => setDialogExpanded((open) => !open)}
          >
            <DocsCodeBlock text={DIALOG_TEXT} label="dialog" />
          </DocsCodePreview>
          <div className="docs-note">
            The dialog can be the site&apos;s own modal. The iframe address stays the stored{' '}
            <code>galleryUrl</code>.
          </div>
        </div>

        <div className="step" id="linkout">
          <div className="step-head">
            <span className="step-num">03</span>
            <span className="step-name">Link to the full plan</span>
          </div>
          <p>
            Add a normal link to the same <code>galleryUrl</code>. It opens the gallery in the
            top-level window. Use it beside the iframe, and use it alone when the listing template
            only has a URL field.
          </p>
          <p>
            Show the plan only when the listing has a stored <code>galleryUrl</code>.
          </p>
        </div>

        <div className="step" id="checklist">
          <h3 className="ref-heading">Embed checklist</h3>
          <ul className="checklist">
            <li>
              <code>galleryUrl</code> stored on the listing, unchanged
            </li>
            <li>
              Iframe <code>src</code> is that exact URL
            </li>
            <li>
              <code>title</code> describes the frame
            </li>
            <li>Container has a real height, or the dialog fills the screen</li>
            <li>
              Host <code>frame-src</code> allows the <code>galleryUrl</code> origin
            </li>
            <li>The same URL is available as a normal link</li>
            <li>API keys stay on the server</li>
            <li>On a phone, the plan opens large: dialog or full page</li>
          </ul>
        </div>
      </section>

      <div className="docs-layer-head" role="presentation">
        <span className="docs-layer-kicker">Contract reference</span>
      </div>

      <section className="phase docs-phase--ref" id="parameters">
        <h2 className="phase-title">The URL</h2>
        <p>
          <code>galleryUrl</code> is an absolute URL. Put that string in <code>iframe.src</code>{' '}
          and in the full-page link.
        </p>
        <DocsCodeBlock text={EXAMPLE_GALLERY} label="galleryUrl">
          <span className="s">{EXAMPLE_GALLERY}</span>
        </DocsCodeBlock>
        <table className="scope-ownership">
          <thead>
            <tr>
              <th>Part</th>
              <th>Example</th>
              <th>What to do with it</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Origin</td>
              <td><code>https://try.magicfurnish.com</code></td>
              <td>
                The MagicFurnish host for this account. Partner accounts use their own host.
                Keep the origin on the URL you were given.
              </td>
            </tr>
            <tr>
              <td>Path</td>
              <td><code>/floor-gallery/</code></td>
              <td>The public gallery. This is the page to show shoppers.</td>
            </tr>
            <tr>
              <td>Id</td>
              <td><code>507f1f77bcf86cd799439011</code></td>
              <td>
                Identifies this gallery. It is not <code>uniqueId</code>. Do not build the path
                from <code>uniqueId</code>.
              </td>
            </tr>
          </tbody>
        </table>

        <h3 className="ref-heading">Where the URL comes from</h3>
        <p>
          The system that creates the floor plan stores <code>galleryUrl</code> on the listing.
          If you already have that string, this guide is the rest of the work.
        </p>
        <p>
          When you have a listing id and a MagicFurnish API key, look the URL up from your server.
          The route is <code>GET /api/v2/ai/layout-by-ref?ref={'{listingId}'}</code> on the API
          origin MagicFurnish gives you with the key. Send <code>X-API-Key</code> and{' '}
          <code>X-API-Secret</code>. Call it from the server. Do not put the key or the secret in
          the listing page.
        </p>
        <table className="scope-ownership">
          <thead>
            <tr>
              <th>status</th>
              <th>Meaning</th>
              <th>What to show</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><code>ready</code></td>
              <td>A furnished plan exists for that listing id.</td>
              <td>The returned <code>galleryUrl</code>.</td>
            </tr>
            <tr>
              <td><code>processing</code></td>
              <td>The plan exists and the furnished image is still being made.</td>
              <td>Nothing yet. Ask again later.</td>
            </tr>
            <tr>
              <td><code>none</code></td>
              <td>No plan for that listing id on this account.</td>
              <td>Nothing.</td>
            </tr>
          </tbody>
        </table>
        <DocsCodeBlock text={LOOKUP_RESPONSE_TEXT} label="ready response">
          {'{'}
          {'\n  '}
          <span className="k">&quot;ref&quot;</span>: <span className="s">&quot;LISTING-1042&quot;</span>,
          {'\n  '}
          <span className="k">&quot;status&quot;</span>: <span className="s">&quot;ready&quot;</span>,
          {'\n  '}
          <span className="k">&quot;galleryUrl&quot;</span>: <span className="s">&quot;{EXAMPLE_GALLERY}&quot;</span>,
          {'\n  '}
          <span className="k">&quot;uniqueId&quot;</span>: <span className="s">&quot;f47ac10b-58cc-4372-a567-0e02b2c3d479&quot;</span>,
          {'\n  '}
          <span className="k">&quot;hasOlderLayouts&quot;</span>: <span className="k">false</span>,
          {'\n  '}
          <span className="k">&quot;olderLayoutsCount&quot;</span>: <span className="k">0</span>
          {'\n}'}
        </DocsCodeBlock>
        <dl className="param-dl">
          <div className="param-row">
            <dt><code>galleryUrl</code></dt>
            <dd>The iframe address. Store it exactly as returned.</dd>
          </div>
          <div className="param-row">
            <dt><code>uniqueId</code></dt>
            <dd>
              An opaque MagicFurnish id. It is not the <code>floor-gallery</code> path segment.
            </dd>
          </div>
          <div className="param-row">
            <dt><code>ref</code></dt>
            <dd>The listing id you asked for.</dd>
          </div>
        </dl>
        <p>
          <code>ready</code>, <code>processing</code>, and <code>none</code> all answer with HTTP
          200, so read <code>status</code> rather than the HTTP code. <code>processing</code> and{' '}
          <code>none</code> return <code>ref</code> and <code>status</code> only. A failure answers
          with <code>status</code> of <code>error</code> and a <code>message</code>: 400 for a
          missing <code>ref</code>, 401 for a bad key or secret, 403 when the key carries no
          account scope, 500 for a server fault. Keep the URL you already stored and ask again
          later.
        </p>
        <p>
          <code>hasOlderLayouts</code> and <code>olderLayoutsCount</code> describe earlier plans
          for the same listing. Most integrations can ignore them.
        </p>
        <div className="docs-note">
          The lookup is scoped to the account on the API key. A missing account scope is rejected.
        </div>
      </section>

      <section className="phase docs-phase--ref" id="galleries">
        <h2 className="phase-title">Replacing a plan</h2>
        <ul>
          <li>
            A new floor plan for the same listing produces a new <code>galleryUrl</code>. Store
            the new URL and point the iframe at it.
          </li>
          <li>
            A shopper who edits a plan after opening it gets a separate gallery. The listing keeps
            the URL you stored.
          </li>
          <li>
            While <code>status</code> is <code>processing</code>, keep showing the previous URL if
            you have one.
          </li>
        </ul>
      </section>

      <div className="docs-layer-head" role="presentation">
        <span className="docs-layer-kicker">Security &amp; operations</span>
      </div>

      <section className="phase docs-phase--ref" id="security">
        <h2 className="phase-title">Security</h2>
        <ul>
          <li>
            Anyone with the <code>galleryUrl</code> can open the gallery. Treat it as a public link.
          </li>
          <li>
            Shoppers do not need a MagicFurnish account. A signed-in MagicFurnish session on
            another site does not carry into the listing page.
          </li>
          <li>
            Host CSP: allow the <code>galleryUrl</code> origin in <code>frame-src</code>.
          </li>
          <li>
            Keep <code>X-API-Key</code> and <code>X-API-Secret</code> on the server that calls{' '}
            <code>layout-by-ref</code>.
          </li>
          <li>
            The gallery does not send a completion event. Ignore other messages from the frame.
          </li>
          <li>
            A listing page can place the gallery URL in an iframe. Use the origin on the URL you
            were given.
          </li>
        </ul>
      </section>

      <section className="phase docs-phase--ref" id="troubleshooting">
        <h2 className="phase-title">Troubleshooting</h2>
        <div className="step">
          <div className="troubleshooting">
            <details>
              <summary>
                <span className="accordion-title">Blank frame</span>
                <span className="chevron" aria-hidden="true" />
              </summary>
              <div className="accordion-body">
                <p>
                  Confirm <code>src</code> is the <code>https</code> <code>galleryUrl</code>, and
                  that the host Content Security Policy allows that origin in <code>frame-src</code>.
                </p>
              </div>
            </details>
            <details>
              <summary>
                <span className="accordion-title">Login page inside the frame</span>
                <span className="chevron" aria-hidden="true" />
              </summary>
              <div className="accordion-body">
                <p>
                  <code>/floor-gallery/…</code> opens for a shopper with no account. Other
                  MagicFurnish paths ask for a login. Replace <code>src</code> with the stored{' '}
                  <code>galleryUrl</code>.
                </p>
              </div>
            </details>
            <details>
              <summary>
                <span className="accordion-title">Scroll is stuck inside the plan</span>
                <span className="chevron" aria-hidden="true" />
              </summary>
              <div className="accordion-body">
                <p>
                  The frame is too short. Raise it toward <code>70vh</code>, or open the plan in
                  the dialog from <a href="#dialog">Open the plan over the page</a>.
                </p>
              </div>
            </details>
            <details>
              <summary>
                <span className="accordion-title">Wrong plan</span>
                <span className="chevron" aria-hidden="true" />
              </summary>
              <div className="accordion-body">
                <p>
                  The path was built from <code>uniqueId</code>, or the listing still has an older{' '}
                  <code>galleryUrl</code>. Read <code>galleryUrl</code> again and store that string.
                </p>
              </div>
            </details>
            <details>
              <summary>
                <span className="accordion-title">Unexpected messages</span>
                <span className="chevron" aria-hidden="true" />
              </summary>
              <div className="accordion-body">
                <p>
                  The gallery can post messages to the listing page. Ignore them. Displaying the
                  plan does not depend on a message.
                </p>
              </div>
            </details>
          </div>
        </div>
      </section>

      <div className="docs-layer-head" role="presentation">
        <span className="docs-layer-kicker">Additional resources</span>
      </div>

      <section className="phase docs-phase--ref" id="platform">
        <h2 className="phase-title">On a phone</h2>
        <p>
          A short frame on a phone traps scrolling: the shopper moves inside the plan instead of
          down the listing. Pick one:
        </p>
        <ul>
          <li>
            Keep it on the page and size the frame from the phone&apos;s screen, leaving room to
            scroll past it.
          </li>
          <li>
            On a narrow screen, show a button that opens the dialog, or send the shopper to the
            full <code>galleryUrl</code>.
          </li>
        </ul>
        <div className="docs-note">
          MagicFurnish does not report the plan&apos;s height to the listing page. The listing
          page chooses the height.
        </div>
      </section>

      <section className="phase docs-phase--ref docs-phase--follow" id="sample">
        <h2 className="phase-title">Sample page</h2>
        <p>
          Save this page and open it on a server you control. Replace both addresses with a real{' '}
          <code>galleryUrl</code> before you judge the layout. The listing template&apos;s Content
          Security Policy still has to allow that origin.
        </p>
        <DocsCodePreview
          label="sample page"
          expanded={sampleExpanded}
          onToggle={() => setSampleExpanded((open) => !open)}
        >
          <DocsCodeBlock text={SAMPLE_PAGE_TEXT} label="sample page" />
        </DocsCodePreview>
      </section>

      <section className="phase docs-phase--ref docs-phase--follow" id="create">
        <h2 className="phase-title">Creating the plan</h2>
        <p>
          This guide starts from a <code>galleryUrl</code> you already have. To create that link
          from your own product, follow <a href="/create">/create</a>. The S.MPLE upload guide is
          at <a href="/smple">/smple</a>.
        </p>
      </section>

      <div className="docs-layer-head" role="presentation">
        <span className="docs-layer-kicker">Production launch</span>
      </div>

      <section className="confirm" id="publishing">
        <h2 className="confirm-title">On the listing</h2>
        <p className="confirm-lead">
          The embed is complete when each listing that has a furnished plan shows it from its
          stored <code>galleryUrl</code>.
        </p>
        <ul className="checklist">
          <li>The listing record holds the <code>galleryUrl</code> for that plan.</li>
          <li>The floor-plan spot on the template uses that URL as <code>iframe.src</code>.</li>
          <li>A normal link uses the same URL.</li>
          <li>Listings with no URL do not show an empty frame.</li>
        </ul>
      </section>

      <section className="confirm" id="kickoff">
        <h2 className="confirm-title">Launch inputs</h2>
        <p className="confirm-lead">
          Coordinate these with MagicFurnish before go-live. They are not required to place a URL
          you already have.
        </p>
        <ol>
          <li>The MagicFurnish host on your <code>galleryUrl</code>s.</li>
          <li>The system that stores <code>galleryUrl</code> on each listing.</li>
          <li>The listing template field or module that will show it.</li>
          <li>
            An API key, when you will resolve a listing id with <code>layout-by-ref</code>.
            Request one from <a href="mailto:support@magicfurnish.com">support@magicfurnish.com</a>.
          </li>
        </ol>
      </section>
    </DocsGuide>
  );
};

export default EmbedGuideDocs;
